// Call Blasting — the page's surface (single provider). `run` is the
// generic action front-door (get_balance, list_voices, …); `startBlastAll`
// is the headline flow ("send a voice blast to everyone expiring this
// month" — the server pulls the recipient set from radius_subscribers,
// dedups, splits into provider-sized batches, fires them sequentially).
// Mirrors the RADIUS Status / IP Phone module shape.

import type { ApiClient } from "./client.js";
import type {
  CallBlastNeedsFixResponse,
  CallBlastRunRequest,
  CallBlastRunResult,
  CallBlastSaveRequest,
  CallBlastSaveResult,
  CallBlastStartResponse,
  CallBlastState,
  CallBlastStateResponse,
  CallBlastTestResult,
} from "./types.js";

export function makeCallBlastApi(client: ApiClient) {
  function creds(req: CallBlastSaveRequest): Record<string, unknown> {
    const b: Record<string, unknown> = { backend_kind: req.backend_kind, base_url: req.base_url };
    if (req.token) b.token = req.token;
    if (req.extra) b.extra = req.extra;
    return b;
  }

  return {
    /** The page view-model: provider + balance + voices/senders + recipient
     *  counts + batch plan + recent broadcasts + this-month activity.
     *  Throws if the `state` envelope is malformed. */
    async state(): Promise<CallBlastState> {
      const r = await client.get<CallBlastStateResponse>("/v1/api/callblast/state");
      if (!r.ok || !r.state) throw new Error(r.error ?? "callblast state unavailable");
      return r.state;
    },
    /** The subscribers whose `mobile` value can't be dialed (with per-reason counts). */
    needsFix() {
      return client.get<CallBlastNeedsFixResponse>("/v1/api/callblast/needs_fix");
    },
    /** Configure the provider (backend / base URL / bearer token) — admin only.
     *  Empty/omitted `token` keeps the stored ciphertext. */
    save(req: CallBlastSaveRequest) {
      return client.post<CallBlastSaveResult>("/v1/api/callblast/save", creds(req));
    },
    /** Probe connectivity without persisting. Empty `token` falls back to the stored one. */
    test(req: CallBlastSaveRequest) {
      return client.post<CallBlastTestResult>("/v1/api/callblast/test", creds(req));
    },
    /** Generic action front-door — prefer `startBlastAll` for the headline flow. */
    run(req: CallBlastRunRequest) {
      return client.post<CallBlastRunResult>("/v1/api/callblast/run", {
        action: req.action,
        args: req.args ?? {},
      });
    },
    /** Fire a voice blast to every subscriber expiring this month — the server
     *  resolves the recipient list itself; we only pass the voice template name
     *  and the caller-ID (sender). Costs money. */
    startBlastAll(args: { voice: string; sender: string }) {
      return client.post<CallBlastStartResponse>("/v1/api/callblast/start_blast_all", {
        voice: args.voice,
        sender: args.sender,
      });
    },
    /** Wipe the rolling telemetry for one action key (e.g. "ensure_connected") — admin only. */
    clearHistory(action: string) {
      return client.post<CallBlastSaveResult>("/v1/api/callblast/clear_history", { action });
    },
  };
}

export type CallBlastApi = ReturnType<typeof makeCallBlastApi>;
